/**
 * `lazy()`, but it survives a deploy.
 *
 * Every route in this app is code-split, so opening a page fetches a file
 * whose name carries a content hash. Deploy a new build and those names
 * change; the old files are gone. Anyone who had the app open across a
 * deploy is running the previous `index.js`, and the moment they navigate
 * somewhere new the browser asks for a chunk that no longer exists.
 *
 * Static Web Apps answers a missing path with `index.html` so that deep
 * links work, which turns the failure into something worse than a 404: the
 * browser gets HTML where it expected a module, refuses it on MIME
 * grounds, and React unmounts into a blank screen. What a user sees is the
 * application disappearing.
 *
 * This happened in production. It became likely when the bundle was split
 * into 146 per-route chunks — the split was right, and it made a latent
 * problem frequent, because there are now far more hashed names that can
 * go missing.
 *
 * So a failed import reloads the page once. The reload fetches the current
 * `index.html`, the user lands where they were going, and the only cost is
 * a flash. The guard is a session flag, because a chunk that is genuinely
 * broken rather than stale would otherwise reload for ever — in that case
 * the error is rethrown and the error boundary shows it.
 */

import { lazy, type ComponentType, type LazyExoticComponent } from 'react'

const RELOAD_FLAG = 'cornerstone.chunk-reloaded'

/** A missing chunk, however the browser phrases it. */
function looksLikeStaleChunk(error: unknown): boolean {
  const message = error instanceof Error ? `${error.name}: ${error.message}` : String(error)
  return (
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    // The SPA fallback case: HTML arrived where a module was expected.
    /Expected a JavaScript(-or-Wasm)? module script/i.test(message) ||
    /MIME type of "text\/html"/i.test(message)
  )
}

// Mirrors React's own lazy signature so a route component keeps its props.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyRoute<T extends ComponentType<any>>(load: () => Promise<{ default: T }>): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      const module = await load()
      // A clean load means whatever we are running is current; let the next
      // stale chunk have its one reload.
      try {
        window.sessionStorage.removeItem(RELOAD_FLAG)
      } catch {
        /* private windows and blocked storage are fine */
      }
      return module
    } catch (error) {
      if (!looksLikeStaleChunk(error)) throw error

      let alreadyTried = false
      try {
        alreadyTried = window.sessionStorage.getItem(RELOAD_FLAG) === '1'
        window.sessionStorage.setItem(RELOAD_FLAG, '1')
      } catch {
        // Without storage we cannot tell a stale chunk from a broken one,
        // and a reload loop is worse than an error page. Show the error.
        throw error
      }

      if (alreadyTried) throw error
      // Replace rather than assign: the dead entry point should not stay in
      // the history for the back button to return to.
      window.location.reload()
      // Never resolves; the reload is already under way.
      return await new Promise<{ default: T }>(() => {})
    }
  })
}
